import dayjs from 'dayjs';

import { ChargeBeeAddonId, ChargeBeePlan, ChargeBeePlanId } from './types';
import { chargeBeePlans } from './plans';
import { getPlanById } from './utils';

/**
 * The four values the September 2026 repricing moves together: base price,
 * included allowance, overage rate and Chargebee overage addon id. They are
 * substituted as one object so a partial override is structurally impossible.
 */
export type Pricing2026Override = {
  readonly costInCents: number;
  readonly includedRenderedPages: number;
  readonly addonCostInCents: number;
  readonly chargeBeeAddonId: ChargeBeeAddonId;
};

/**
 * The three self-serve plans the September 2026 repricing applies to. Closing the
 * override table's key type to these makes an override for any other plan id a
 * compile error at the offending line, rather than a runtime surprise.
 */
export type RepricedPlanId = ChargeBeePlanId.Starter | ChargeBeePlanId.Growth | ChargeBeePlanId.Pro;

/** Narrows a plan id down to the three plans that can carry a 2026 override. */
export const isRepricedPlanId = (planId: ChargeBeePlanId): planId is RepricedPlanId =>
  planId === ChargeBeePlanId.Starter || planId === ChargeBeePlanId.Growth || planId === ChargeBeePlanId.Pro;

export const CANCELLED_ACCOUNT_RETENTION_MONTHS = 12;

/**
 * The single definition of "cancelled recently enough to still be told about the
 * repricing". Both the dashboard predicate below and the fan-out's audience SQL
 * (queue-service pricingUpdate2026Audiences) consume this helper, because two
 * independent month-subtractions quietly disagree: Date#setUTCMonth overflows a
 * leap day (Feb 29 minus 12 months lands on Mar 1) while dayjs clamps to the last
 * day of the target month, splitting one account across the two audiences.
 */
export const cancelledAccountRetentionStart = (now: Date): Date =>
  dayjs(now).subtract(CANCELLED_ACCOUNT_RETENTION_MONTHS, 'months').toDate();

const isWithinCancelledAccountRetention = (cancelledAt: Date | null | undefined, now: Date): boolean => {
  if (!cancelledAt) {
    return true;
  }

  return cancelledAt > cancelledAccountRetentionStart(now);
};

/**
 * Whether the September 2026 repricing moves this subscription's rate, and so whether it
 * belongs to the audience told about the change. Lives here, beside the values that do the
 * repricing, so every surface that announces the change imports one definition rather than
 * restating it as a plan-name comparison or an inline plan-id list.
 */
export const isAffectedByPricing2026 = (
  subscription: {
    readonly chargebeePlanId: string;
    readonly customPrice?: number | null;
    readonly cancelledAt?: Date | null;
  },
  now: Date = new Date()
): boolean =>
  isRepricedPlanId(subscription.chargebeePlanId as ChargeBeePlanId) &&
  !Number.isFinite(subscription.customPrice) &&
  isWithinCancelledAccountRetention(subscription.cancelledAt, now);

/** Per-plan 2026 values. Only 2025-tier self-serve plans ever have an entry. */
export const PRICING_2026_OVERRIDES: Record<RepricedPlanId, Pricing2026Override> = {
  [ChargeBeePlanId.Starter]: {
    costInCents: 5900,
    includedRenderedPages: 30000,
    addonCostInCents: 225,
    chargeBeeAddonId: ChargeBeeAddonId.starter2026Overages,
  },
  [ChargeBeePlanId.Growth]: {
    costInCents: 16900,
    includedRenderedPages: 100000,
    addonCostInCents: 175,
    chargeBeeAddonId: ChargeBeeAddonId.growth2026Overages,
  },
  [ChargeBeePlanId.Pro]: {
    costInCents: 41900,
    includedRenderedPages: 500000,
    addonCostInCents: 150,
    chargeBeeAddonId: ChargeBeeAddonId.pro2026Overages,
  },
};

/**
 * Reverse lookup: which plan does an overage addon id belong to, in either
 * pricing generation. The 2026 addon ids live only in the override table, so a
 * catalog-only search silently misses every repriced subscription.
 */
export const findPlanByChargeBeeAddonId = (addonId: string): ChargeBeePlan | undefined => {
  const catalogPlan = chargeBeePlans.find((plan) => plan.chargeBeeAddonId === addonId);

  if (catalogPlan) {
    return catalogPlan;
  }

  const overriddenPlanId = Object.keys(PRICING_2026_OVERRIDES).find(
    (planId) => PRICING_2026_OVERRIDES[planId as RepricedPlanId]?.chargeBeeAddonId === addonId
  ) as RepricedPlanId | undefined;

  if (!overriddenPlanId) {
    return undefined;
  }

  // The addon id alone identifies the generation, so the plan handed back carries
  // that generation's values: an invoice must not report one generation's allowance
  // as included while measuring its excess against another's.
  return { ...getPlanById(overriddenPlanId), ...PRICING_2026_OVERRIDES[overriddenPlanId] };
};
